import { useHotkeys } from 'react-hotkeys-hook';
import React, { useRef } from 'react';
interface HotKeysProps {
  onNavigate?: (direction: 'previous' | 'next') => void;
  onDayNavigate?: (direction: 'previous' | 'next') => void;
  onJumpToDay?: (position: 'first' | 'last') => void;
  onToggleAccordion?: () => void;
  onToggleAllAccordions?: () => void;
  onToggleCurrentDay?: () => void;
  onScrollToTop?: () => void;
  onToggleScroll?: () => void;
  onExitFullscreen?: () => void;
  onToggleImageFullscreen?: () => void;
  onShowHotkeys?: () => void;
  onCloseModal?: () => void;
  isFullscreen?: boolean;
  isModal?: boolean;
  selectedMediaType?: string;
  selectedMediaSrc?: string | null;
  onToggleTranscript?: () => void;
  onToggleTranscriptMarkers?: () => void;
  onChangeTranscriptPosition?: () => void;
  onFeedbackClick?: () => void;
}

// Define hotkeys configuration that can be exported and used by the modal
export const hotkeysList = [{
  section: 'Timeline',
  shortcuts: [{
    description: 'Go to previous/next event',
    keys: ['←', '→'],
    divider: '/'
  }, {
    description: 'Go to previous/next day',
    keys: [['shift', '↑'], ['↓']],
    divider: '/'
  }, {
    description: 'Jump to first/last day',
    keys: [['ctrl', 'shift', '↑'], ['↓']],
    divider: '/'
  }, {
    description: 'Toggle timeline navigation visibility',
    keys: ['t']
  }, {
    description: 'Expand/collapse all days',
    keys: ['e']
  }, {
    description: 'Expand/collapse current day',
    keys: ['shift', 'e']
  }, {
    description: 'Toggle back to top/bottom',
    keys: ['b']
  }, {
    description: 'Toggle fullscreen media viewer',
    keys: ['space']
  }, {
    description: 'Exit fullscreen and modals',
    keys: ['esc']
  }]
}, {
  section: 'View Controls',
  shortcuts: [{
    description: 'Show keyboard shortcuts',
    keys: ['ctrl', '/']
  }]
}];

// Define a type for hotkey handlers
type HotkeyHandler = {
  key: string; // Only allow string keys
  handler: (e: KeyboardEvent) => void;
  condition?: boolean;
};
const HotKeys: React.FC<HotKeysProps> = ({
  onNavigate,
  onDayNavigate,
  onJumpToDay,
  onToggleAccordion,
  onToggleAllAccordions,
  onToggleCurrentDay,
  onScrollToTop,
  onToggleScroll,
  onExitFullscreen,
  onToggleImageFullscreen,
  onShowHotkeys,
  onCloseModal,
  isFullscreen,
  isModal,
  selectedMediaType,
  selectedMediaSrc,
  onToggleTranscript,
  onToggleTranscriptMarkers,
  onChangeTranscriptPosition,
  onFeedbackClick
}) => {
  const hotkeys = {
    previousSlide: ['left'],
    nextSlide: ['right'],
    previousDay: 'shift+up',
    nextDay: 'shift+down',
    firstDay: 'ctrl+shift+up',
    lastDay: 'ctrl+shift+down',
    toggleAccordion: 't',
    expandCollapseAll: 'e',
    toggleCurrentDay: 'shift+e',
    backToTop: 'b',
    toggleScroll: 'b',
    exitFullscreen: 'esc',
    toggleImageFullscreen: 'space',
    showHotkeys: 'ctrl+/',
    toggleTranscript: 'ctrl+t',
    toggleMarkers: 'ctrl+m',
    changePosition: 'ctrl+p'
  };
  const feedbackDialogRef = useRef<HTMLDialogElement>(document.getElementById('feedback-button')?.shadowRoot?.querySelector('dialog') as HTMLDialogElement);
  const feedbackVisible = () => {
    if (!feedbackDialogRef.current) {
      feedbackDialogRef.current = document.getElementById('feedback-button')?.shadowRoot?.querySelector('dialog') as HTMLDialogElement;
    }
    if (feedbackDialogRef.current) {
      const isFeedbackVisible = feedbackDialogRef.current?.open || false;
      return isFeedbackVisible;
    }
    return false;
  };
  useHotkeys('ctrl+shift+f', e => {
    if (!feedbackVisible() && onNavigate) {
      e.preventDefault();
      console.log('Feedback hotkey triggered');
      onFeedbackClick?.();
    }
  }, {
    enabled: isModal,
    // Only enable when it's a modal
    enableOnFormTags: true,
    // Enable on form elements
    enableOnContentEditable: true // Enable on editable content
  });

  // Register hotkeys properly
  useHotkeys(hotkeys.previousSlide.join(','), e => {
    if (!feedbackVisible() && onNavigate) {
      e.preventDefault();
      console.log('Previous slide hotkey triggered');
      onNavigate('previous');
    }
  }, {
    enableOnFormTags: true
  });
  useHotkeys(hotkeys.nextSlide.join(','), e => {
    if (!feedbackVisible() && onNavigate) {
      e.preventDefault();
      console.log('Next slide hotkey triggered');
      onNavigate('next');
    }
  }, {
    enableOnFormTags: true
  });
  useHotkeys(hotkeys.previousDay, e => {
    if (!feedbackVisible() && onDayNavigate) {
      e.preventDefault();
      onDayNavigate('previous');
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.nextDay, e => {
    if (!feedbackVisible() && onDayNavigate) {
      e.preventDefault();
      onDayNavigate('next');
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.firstDay, e => {
    if (!feedbackVisible() && onJumpToDay) {
      e.preventDefault();
      onJumpToDay('first');
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.lastDay, e => {
    if (!feedbackVisible() && onJumpToDay) {
      e.preventDefault();
      onJumpToDay('last');
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.toggleAccordion, e => {
    if (!feedbackVisible() && onToggleAccordion) {
      e.preventDefault();
      onToggleAccordion();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.expandCollapseAll, e => {
    if (!feedbackVisible() && onToggleAllAccordions) {
      e.preventDefault();
      onToggleAllAccordions();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.toggleCurrentDay, e => {
    if (!feedbackVisible() && onToggleCurrentDay) {
      e.preventDefault();
      onToggleCurrentDay();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.backToTop, e => {
    if (!feedbackVisible() && onScrollToTop) {
      e.preventDefault();
      onScrollToTop();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.toggleScroll, e => {
    if (!feedbackVisible() && onToggleScroll) {
      e.preventDefault();
      onToggleScroll();
    }
  }, {
    enableOnFormTags: false
  });

  // Separate handler for modal escape key
  useHotkeys('esc', e => {
    if (!feedbackVisible() && isModal && onCloseModal) {
      e.preventDefault();
      onCloseModal();
    }
  }, {
    enabled: isModal,
    // Only enable when it's a modal
    enableOnFormTags: true,
    // Enable on form elements
    enableOnContentEditable: true // Enable on editable content
  });

  // Keep the existing exitFullscreen handler for non-modal cases
  useHotkeys(hotkeys.exitFullscreen, e => {
    if (!feedbackVisible() && isFullscreen && onExitFullscreen) {
      e.preventDefault();
      onExitFullscreen();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.toggleImageFullscreen, e => {
    if (!feedbackVisible() && selectedMediaSrc && onToggleImageFullscreen) {
      e.preventDefault();
      onToggleImageFullscreen();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.showHotkeys, e => {
    if (!feedbackVisible() && onShowHotkeys) {
      e.preventDefault();
      onShowHotkeys();
    }
  }, {
    enableOnFormTags: false
  });

  // Register hotkeys for media player controls
  useHotkeys(hotkeys.toggleTranscript, e => {
    if (!feedbackVisible() && onToggleTranscript) {
      e.preventDefault();
      console.log('Toggle Transcript Hotkey Triggered');
      onToggleTranscript();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.toggleMarkers, e => {
    if (!feedbackVisible() && onToggleTranscriptMarkers) {
      e.preventDefault();
      console.log('Toggle Markers Hotkey Triggered');
      onToggleTranscriptMarkers();
    }
  }, {
    enableOnFormTags: false
  });
  useHotkeys(hotkeys.changePosition, e => {
    if (!feedbackVisible() && onChangeTranscriptPosition) {
      e.preventDefault();
      console.log('Change Position Hotkey Triggered');
      onChangeTranscriptPosition();
    }
  }, {
    enableOnFormTags: false
  });
  return <></>;
};
export { HotKeys, type HotKeysProps };